/**
 * Org feature keys — the frontend mirror of the backend catalog
 * (magnetix-api/src/config/org_features_catalog.ts).
 *
 * These keys tag sidebar menu items so they can be hidden when the user's
 * organization has the functionality switched off by a Super Admin. Keys MUST
 * stay in sync with the backend (they're persisted in `organization_features`).
 *
 * The authoritative catalog (labels/descriptions/grouping) is fetched at
 * runtime from `/super-admin/org-features/catalog` for the management screen;
 * this file only needs the stable keys used for menu tagging.
 */
export const OrgFeature = {
  USERS: 'users',
  TEAMS: 'teams',
  ASSIGNMENTS: 'assignments',
  APPROVALS: 'approvals',
  PERFORMANCE: 'performance',

  CONTENT_MANAGEMENT: 'content_management',
  COURSES: 'courses',
  CATALOG: 'catalog',
  GROUP_PROJECTS: 'group_projects',
  CERTIFICATIONS: 'certifications',

  BULK_UPLOADER: 'bulk_uploader',
  MARKETING: 'marketing',
  ILT: 'ilt',
  KNOWLEDGE_BASE: 'knowledge_base',

  REPORTS: 'reports',

  ASSESSMENTS: 'assessments',
  AI_LEARNING_PATHS: 'ai_learning_paths',
  LINGO_LAB: 'lingo_lab',
  AI_PROCTOR: 'ai_proctor',
  CARVE_ASSESSMENT: 'carve_assessment',
} as const;

export type OrgFeatureKey = (typeof OrgFeature)[keyof typeof OrgFeature];

/**
 * Decide whether a menu item is visible given the org's allowed feature set.
 *  - items with no `feature` tag are always visible (Dashboard, Account, etc.)
 *  - `allowed === undefined` => not loaded yet / super admin => show everything
 *  - otherwise the feature must be present in the allowed set
 */
export const isFeatureAllowed = (
  feature: string | undefined,
  allowed: string[] | undefined
): boolean => {
  if (!feature) return true;
  if (!allowed) return true;
  return allowed.includes(feature);
};
